import React, { useEffect, useState } from "react";
import TaskRenderer from "./TaskRenderer";
import TransitionButtons from "./TransitionButtons";
import SlaWarningBanner from "./SlaWarningBanner";
import { ACTIONS } from "../../utils/executionReducer";
import useAuth from "../../hooks/useAuth";
import api from "../../api/client";

export default function StageExecutionView({
  workflow,
  executionState,
  dispatch,
}) {
  const [taskValues, setTaskValues] = useState({});
  const [errors, setErrors] = useState({});

  const currentStage = workflow.stages.find(
    (s) => s.id === executionState.currentStageId,
  );
  const currentHistoryEntry =
    executionState.history[executionState.history.length - 1] || {};

  const { user } = useAuth();

  const [stageRoleNames, setStageRoleNames] = useState([]);
  const [eligible, setEligible] = useState(true);

  useEffect(() => {
    if (currentStage && currentStage.assigneeRoles?.length > 0) {
      api
        .get("/roles")
        .then((r) => {
          const map = {};
          r.data.forEach((role) => {
            map[role.id] = role.name;
          });
          const names = currentStage.assigneeRoles
            .map((id) => map[id])
            .filter(Boolean);
          setStageRoleNames(names);
          const userRoleName = user?.role || "";
          setEligible(names.length === 0 || names.includes(userRoleName));
        })
        .catch((e) => console.error("failed to load roles", e));
    } else {
      setStageRoleNames([]);
      setEligible(true);
    }
  }, [currentStage, user]);

  useEffect(() => {
    // when stage changes, reset local task values from history entry
    setTaskValues(currentHistoryEntry.taskData || {});
    setErrors({});
  }, [currentStage?.id]);

  if (!currentStage) return <div>Invalid stage</div>;

  // Check validity without modifying state
  const checkValid = () => {
    const errs = {};
    (currentStage.tasks || []).forEach((t) => {
      if (t.mandatory) {
        const v = taskValues[t.id];
        if (t.type === "text") {
          if (!v || v.trim() === "") errs[t.id] = "Required";
        } else if (t.type === "file") {
          if (!v) errs[t.id] = "Required";
        }
      }
    });
    return errs;
  };

  // Validate with error display only on transition attempt
  const validate = () => {
    const errs = checkValid();
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleTransition = (connection) => {
    if (!validate()) return;
    // if connection is null -> terminal complete
    if (!connection) {
      // notify exit
      if (currentStage.notifyOnExit) {
        alert(`${currentStage.name} completed`);
      }
      dispatch({
        type: ACTIONS.COMPLETE_WORKFLOW,
        payload: { taskData: taskValues },
      });
      return;
    }
    const target = workflow.stages.find(
      (s) => s.id === connection.targetStageId,
    );
    if (!target) return;
    const alreadyVisited = executionState.visitCounts[target.id] > 0;

    // notify on exit/entry
    if (currentStage.notifyOnExit) {
      alert(`${currentStage.name} completed`);
    }
    if (target.notifyOnEntry) {
      alert(`${target.name} is now active`);
    }

    dispatch({
      type: alreadyVisited ? ACTIONS.REVISIT_STAGE : ACTIONS.COMPLETE_STAGE,
      payload: {
        workflow,
        transition: connection,
        targetStage: target,
        taskData: taskValues,
      },
    });
    window.scrollTo(0, 0);
  };

  // Use checkValid (without setState) for render-time checks
  const allValid = Object.keys(checkValid()).length === 0;

  return (
    <div className="stage-execution-view">
      <SlaWarningBanner
        stage={currentStage}
        enteredAt={currentHistoryEntry.enteredAt}
      />
      {!eligible && (
        <div className="warning-banner" style={{ marginBottom: 8 }}>
          ⚠ You are not in an eligible role for this stage. Contact your admin.
        </div>
      )}
      <h2 style={{ color: currentStage.color }}>{currentStage.name}</h2>
      {stageRoleNames.length > 0 && (
        <div style={{ fontSize: 12, color: "#6b7280" }}>
          This stage requires: {stageRoleNames.join(", ")}
        </div>
      )}
      {currentStage.description && <p>{currentStage.description}</p>}
      <div className="badges">
        <span className="badge">
          {currentStage.estimatedDuration} {currentStage.durationUnit}
        </span>
        <span className="badge">
          Up to {currentStage.maxConcurrentAssignees} people
        </span>
        {executionState.visitCounts[currentStage.id] > 1 && (
          <span className="badge">
            Revisit #{executionState.visitCounts[currentStage.id]}
          </span>
        )}
      </div>

      <TaskRenderer
        tasks={currentStage.tasks}
        values={taskValues}
        errors={errors}
        onChange={(tid, v) => setTaskValues((prev) => ({ ...prev, [tid]: v }))}
      />

      <TransitionButtons
        workflow={workflow}
        currentStageId={currentStage.id}
        history={executionState.history}
        taskValid={allValid}
        onTransition={handleTransition}
      />
    </div>
  );
}
